import {
  impactChunkScale,
  impactFineMassScale,
  resolveImpact,
  type ImpactParams,
} from './impactRegime';
import type { ParticleMaterialProfile } from './profiles';

/**
 * Burst mass ledger. Chunks take `signature.chunkFraction`; the remainder
 * splits fines / grains by `fineFraction`.
 * `fineMass + grainMass + chunkMass === burstMass` exactly.
 *
 * Grain *count* still uses the full burst mass via `planBurst` so Phase 1
 * spray density is unchanged. This split only assigns how much mass each
 * population is responsible for depositing.
 */
export interface BurstMassSplit {
  readonly burstMass: number;
  readonly grainMass: number;
  readonly fineMass: number;
  readonly chunkMass: number;
  readonly fineFraction: number;
  readonly chunkFraction: number;
}

export function splitBurstMass(
  mass: number,
  profile: ParticleMaterialProfile,
  impact?: ImpactParams,
): BurstMassSplit {
  const burstMass = Math.max(0, mass);
  const imp = resolveImpact(impact);
  const chunkFraction = Math.min(1, Math.max(0, profile.signature.chunkFraction * impactChunkScale(imp)));
  const fineFraction = Math.min(1, Math.max(0, profile.fineFraction * impactFineMassScale(imp)));
  const chunkMass = burstMass * chunkFraction;
  const rest = burstMass - chunkMass;
  const fineMass = rest * Math.min(1, fineFraction);
  const grainMass = rest - fineMass;
  return { burstMass, grainMass, fineMass, chunkMass, fineFraction: Math.min(1, fineFraction), chunkFraction };
}

export interface DepositAssignment {
  readonly grainEach: number;
  readonly fineEach: number;
  readonly chunkEach: number;
  readonly grainTotal: number;
  readonly fineTotal: number;
  readonly chunkTotal: number;
  readonly deposited: number;
}

/**
 * Share the split masses across the spawned counts. Empty populations
 * contribute 0. When chunks were budgeted but none spawned, their mass
 * folds into grains so the ledger still sums to burstMass.
 */
export function assignDepositMass(
  split: BurstMassSplit,
  grainCount: number,
  fineCount: number,
  chunkCount = 0,
): DepositAssignment {
  const gN = Math.max(0, grainCount | 0);
  const fN = Math.max(0, fineCount | 0);
  const cN = Math.max(0, chunkCount | 0);
  const chunkTotal = cN > 0 ? split.chunkMass : 0;
  const leftoverChunks = cN > 0 ? 0 : split.chunkMass;
  const grainPool = split.grainMass + leftoverChunks;
  const grainEach = gN > 0 ? grainPool / gN : 0;
  const fineEach = fN > 0 ? split.fineMass / fN : 0;
  const chunkEach = cN > 0 ? split.chunkMass / cN : 0;
  const grainTotal = grainEach * gN;
  const fineTotal = fineEach * fN;
  return {
    grainEach,
    fineEach,
    chunkEach,
    grainTotal,
    fineTotal,
    chunkTotal,
    deposited: grainTotal + fineTotal + chunkTotal,
  };
}
